import React, { useState } from 'react';
import { CopyIcon, CheckIcon } from 'lucide-react';
import { DocsLayout } from '../components/DocsLayout';
// ─── Code Block ──────────────────────────────────────────────────
function CodeBlock({
  code,
  language
}: {
  code: string;
  language: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="relative group rounded-lg border border-gate-border bg-[#0d1117] overflow-hidden" data-id="element-318">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gate-border/50 bg-gate-surface/30" data-id="element-319">
        <span className="text-xs font-mono text-gate-text-muted" data-id="element-320">
          {language}
        </span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-gate-text-muted hover:text-gate-teal transition-colors" aria-label="Copy code" data-id="element-321">
          {copied ? <>
              <CheckIcon className="w-3.5 h-3.5" data-id="element-322" />
              <span data-id="element-323">Copied</span>
            </> : <>
              <CopyIcon className="w-3.5 h-3.5" data-id="element-324" />
              <span data-id="element-325">Copy</span>
            </>}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed" data-id="element-326">
        <code className="font-mono text-gate-text-primary" data-id="element-327">{code}</code>
      </pre>
    </div>;
}
// ─── Inline Code Helper ──────────────────────────────────────────
function IC({
  children
}: {
  children: React.ReactNode;
}) {
  return <code className="font-mono text-sm text-gate-teal bg-gate-teal/10 px-1.5 py-0.5 rounded" data-id="element-328">
      {children}
    </code>;
}
// ─── Schema Field Row ────────────────────────────────────────────
function Field({
  name,
  type,
  children
}: {
  name: string;
  type: string;
  children: React.ReactNode;
}) {
  return <div className="py-3 border-b border-gate-border/50 last:border-0" data-id="element-329">
      <div className="flex items-baseline gap-2 mb-1" data-id="element-330">
        <code className="font-mono text-sm text-gate-teal" data-id="element-331">{name}</code>
        <span className="text-xs text-gate-text-muted font-mono" data-id="element-332">{type}</span>
      </div>
      <p className="text-sm text-gate-text-secondary leading-relaxed" data-id="element-333">
        {children}
      </p>
    </div>;
}
// ─── TOC Items ───────────────────────────────────────────────────
const tocItems = [{
  id: 'models-api',
  label: 'Models API Standard'
}, {
  id: 'api-response',
  label: 'API Response Schema'
}, {
  id: 'root-response',
  label: 'Root Response Object'
}, {
  id: 'model-object',
  label: 'Model Object Schema'
}, {
  id: 'architecture-object',
  label: 'Architecture Object'
}, {
  id: 'pricing-object',
  label: 'Pricing Object'
}, {
  id: 'hardware-object',
  label: 'Hardware Object'
}, {
  id: 'supported-params',
  label: 'Supported Parameters'
}, {
  id: 'calling-models',
  label: 'Calling a Model'
}, {
  id: 'for-providers',
  label: 'For Providers'
}];
// ─── Page ────────────────────────────────────────────────────────
export function DocsModelsPage() {
  const listModelsCode = `curl https://api.openfpga.ai/v1/models \\
  -H "Authorization: Bearer $OPENFPGA_API_KEY"`;
  const rootResponseCode = `{
  "data": [
    /* Array of Model objects */
  ]
}`;
  const modelObjectCode = `{
  "id": "meta-llama/llama-3.3-70b-instruct",
  "name": "Llama 3.3 70B Instruct",
  "created": 1733961600,
  "description": "Meta's advanced 70B instruction-tuned model...",
  "context_length": 128000,
  "architecture": {
    "input_modalities": ["text"],
    "output_modalities": ["text"],
    "tokenizer": "llama3"
  },
  "pricing": {
    "prompt": "0.00000059",
    "completion": "0.00000061",
    "unit": "token"
  },
  "hardware": {
    "target": "AMD/Xilinx Alveo U250",
    "status": "available",
    "synthesis_version": "1.2.0"
  },
  "top_provider": {
    "max_completion_tokens": 16384,
    "is_moderated": false
  },
  "supported_parameters": [
    "temperature", "top_p", "max_tokens",
    "stop", "frequency_penalty", "presence_penalty"
  ]
}`;
  const architectureCode = `{
  "input_modalities": ["text"],
  "output_modalities": ["text"],
  "tokenizer": "llama3"
}`;
  const pricingCode = `{
  "prompt": "0.00000059",
  "completion": "0.00000061",
  "unit": "token"
}`;
  const hardwareCode = `{
  "target": "AMD/Xilinx Alveo U250",
  "status": "available",
  "synthesis_version": "1.2.0"
}`;
  const curlCallCode = `curl https://api.openfpga.ai/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $OPENFPGA_API_KEY" \\
  -d '{
    "model": "meta-llama/llama-3.3-70b-instruct",
    "messages": [
      {
        "role": "user",
        "content": "Explain FPGA synthesis in simple terms."
      }
    ],
    "temperature": 0.7,
    "max_tokens": 512
  }'`;
  const pythonCallCode = `from openai import OpenAI

client = OpenAI(
    base_url="https://api.openfpga.ai/v1",
    api_key="your-openfpga-api-key",
)

response = client.chat.completions.create(
    model="meta-llama/llama-3.3-70b-instruct",
    messages=[
        {"role": "user", "content": "Explain FPGA synthesis in simple terms."}
    ],
    temperature=0.7,
    max_tokens=512,
)

print(response.choices[0].message.content)`;
  const tsCallCode = `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://api.openfpga.ai/v1",
  apiKey: process.env.OPENFPGA_API_KEY,
});

async function main() {
  const response = await client.chat.completions.create({
    model: "meta-llama/llama-3.3-70b-instruct",
    messages: [
      { role: "user", content: "Explain FPGA synthesis in simple terms." }
    ],
    temperature: 0.7,
    max_tokens: 512,
  });

  console.log(response.choices[0].message.content);
}

main();`;
  const streamCode = `curl https://api.openfpga.ai/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $OPENFPGA_API_KEY" \\
  -d '{
    "model": "meta-llama/llama-3.3-70b-instruct",
    "messages": [{"role": "user", "content": "Hello!"}],
    "stream": true
  }'`;
  return <DocsLayout activePage="Models" breadcrumb="Models" tocItems={tocItems} data-id="element-334">
      {/* Title */}
      <h1 className="text-4xl font-bold text-white mb-2 tracking-tight" data-id="element-335">
        Models
      </h1>
      <p className="text-lg text-gate-text-secondary mb-10" data-id="element-336">
        One API for FPGA-accelerated inference
      </p>

      {/* Intro */}
      <p className="text-gate-text-secondary leading-relaxed mb-8" data-id="element-337">
        Explore and browse available models{' '}
        <a href="#/models" className="text-gate-teal hover:underline" data-id="element-338">
          on our models page
        </a>
        , or query them programmatically with our API. OpenFPGA uses an
        OpenAI-compatible API, so any existing OpenAI SDK or client works out of
        the box — just change the base URL.
      </p>

      {/* ── Models API Standard ── */}
      <section className="mb-14" data-id="element-339">
        <h2 id="models-api" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-340">
          Models API Standard
        </h2>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-341">
          Our Models API provides metadata about every model available on
          OpenFPGA, including pricing, hardware targets, synthesis status, and
          supported parameters. The endpoint is publicly accessible:
        </p>
        <CodeBlock code={listModelsCode} language="bash" data-id="element-342" />
      </section>

      {/* ── API Response Schema ── */}
      <section className="mb-14" data-id="element-343">
        <h2 id="api-response" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-344">
          API Response Schema
        </h2>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-345">
          The Models API returns a standardized JSON response format that
          provides comprehensive metadata for each available model. This schema
          is designed for reliable integration with production applications.
        </p>

        {/* Root Response */}
        <h3 id="root-response" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-346">
          Root Response Object
        </h3>
        <CodeBlock code={rootResponseCode} language="json" data-id="element-347" />
        <div className="mt-4 mb-8" data-id="element-348">
          <Field name="data" type="Model[]" data-id="element-349">
            An array of Model objects, one for each model available on OpenFPGA.
          </Field>
        </div>

        {/* Model Object */}
        <h3 id="model-object" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-350">
          Model Object Schema
        </h3>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-351">
          Each model in the <IC data-id="element-352">data</IC> array contains the following
          standardized fields:
        </p>
        <CodeBlock code={modelObjectCode} language="json" data-id="element-353" />
        <div className="mt-4 mb-8" data-id="element-354">
          <Field name="id" type="string" data-id="element-355">
            Unique model identifier in <IC data-id="element-356">provider/model-name</IC> format. Use
            this when making inference requests.
          </Field>
          <Field name="name" type="string" data-id="element-357">
            Human-readable display name for the model.
          </Field>
          <Field name="created" type="number" data-id="element-358">
            Unix timestamp of when the model was added to OpenFPGA.
          </Field>
          <Field name="description" type="string" data-id="element-359">
            Brief description of the model's capabilities and FPGA optimization
            status.
          </Field>
          <Field name="context_length" type="number" data-id="element-360">
            Maximum number of tokens (input + output) the model supports.
          </Field>
          <Field name="architecture" type="Architecture" data-id="element-361">
            Input/output modalities and tokenizer information.
          </Field>
          <Field name="pricing" type="Pricing" data-id="element-362">
            Per-token pricing for prompts and completions.
          </Field>
          <Field name="hardware" type="Hardware" data-id="element-363">
            FPGA hardware target, synthesis status, and version.
          </Field>
          <Field name="top_provider" type="TopProvider" data-id="element-364">
            Provider-level constraints like max completion tokens.
          </Field>
          <Field name="supported_parameters" type="string[]" data-id="element-365">
            List of inference parameters this model accepts.
          </Field>
        </div>

        {/* Architecture Object */}
        <h3 id="architecture-object" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-366">
          Architecture Object
        </h3>
        <CodeBlock code={architectureCode} language="json" data-id="element-367" />
        <div className="mt-4 mb-8" data-id="element-368">
          <Field name="input_modalities" type="string[]" data-id="element-369">
            Supported input types: <IC data-id="element-370">text</IC>, <IC data-id="element-371">image</IC>, or both.
          </Field>
          <Field name="output_modalities" type="string[]" data-id="element-372">
            Supported output types: <IC data-id="element-373">text</IC>, <IC data-id="element-374">image</IC>, or both.
          </Field>
          <Field name="tokenizer" type="string" data-id="element-375">
            Tokenizer used by the model (e.g. <IC data-id="element-376">llama3</IC>, <IC data-id="element-377">mistral</IC>
            ).
          </Field>
        </div>

        {/* Pricing Object */}
        <h3 id="pricing-object" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-378">
          Pricing Object
        </h3>
        <CodeBlock code={pricingCode} language="json" data-id="element-379" />
        <div className="mt-4 mb-8" data-id="element-380">
          <Field name="prompt" type="string" data-id="element-381">
            Cost per token for input/prompt tokens, as a decimal string in USD.
          </Field>
          <Field name="completion" type="string" data-id="element-382">
            Cost per token for output/completion tokens, as a decimal string in
            USD.
          </Field>
          <Field name="unit" type="string" data-id="element-383">
            Pricing unit — always <IC data-id="element-384">token</IC>.
          </Field>
        </div>

        {/* Hardware Object */}
        <h3 id="hardware-object" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-385">
          Hardware Object
        </h3>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-386">
          FPGA-specific metadata unique to OpenFPGA. This object describes the
          hardware target and synthesis status for each model.
        </p>
        <CodeBlock code={hardwareCode} language="json" data-id="element-387" />
        <div className="mt-4 mb-8" data-id="element-388">
          <Field name="target" type="string" data-id="element-389">
            FPGA hardware target (e.g. <IC data-id="element-390">AMD/Xilinx Alveo U250</IC>,{' '}
            <IC data-id="element-391">Intel/Altera Agilex 7</IC>).
          </Field>
          <Field name="status" type="string" data-id="element-392">
            Synthesis status: <IC data-id="element-393">available</IC> (ready for inference) or{' '}
            <IC data-id="element-394">coming-soon</IC> (in progress).
          </Field>
          <Field name="synthesis_version" type="string" data-id="element-395">
            Version of the FPGA synthesis pipeline used to compile this model.
          </Field>
        </div>

        {/* Supported Parameters */}
        <h3 id="supported-params" className="text-xl font-bold text-white mb-3 scroll-mt-24" data-id="element-396">
          Supported Parameters
        </h3>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-397">
          The <IC data-id="element-398">supported_parameters</IC> array lists which inference
          parameters each model accepts. OpenFPGA supports the standard
          OpenAI-compatible parameters:
        </p>
        <div className="rounded-lg border border-gate-border bg-[#0d1117] overflow-hidden" data-id="element-399">
          <div className="p-4 space-y-2" data-id="element-400">
            {[['temperature', 'Control response randomness (0.0–2.0)'], ['top_p', 'Nucleus sampling threshold (0.0–1.0)'], ['max_tokens', 'Maximum tokens to generate'], ['stop', 'Custom stop sequences (string or array)'], ['frequency_penalty', 'Reduce repetition of token sequences (-2.0–2.0)'], ['presence_penalty', 'Encourage new topics (-2.0–2.0)'], ['stream', 'Enable server-sent events streaming']].map(([param, desc]) => <div key={param} className="flex items-start gap-3" data-id="element-401">
                <code className="font-mono text-sm text-gate-teal bg-gate-teal/10 px-1.5 py-0.5 rounded flex-shrink-0" data-id="element-402">
                  {param}
                </code>
                <span className="text-gate-text-secondary text-sm" data-id="element-403">
                  — {desc}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── Calling a Model ── */}
      <section className="mb-14" data-id="element-404">
        <h2 id="calling-models" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-405">
          Calling a Model
        </h2>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-406">
          OpenFPGA uses the OpenAI-compatible <IC data-id="element-407">/v1/chat/completions</IC>{' '}
          endpoint. Use any OpenAI SDK by pointing the base URL to{' '}
          <IC data-id="element-408">https://api.openfpga.ai/v1</IC>.
        </p>

        <h3 className="text-lg font-bold text-white mb-3" data-id="element-409">Using curl</h3>
        <div className="mb-6" data-id="element-410">
          <CodeBlock code={curlCallCode} language="bash" data-id="element-411" />
        </div>

        <h3 className="text-lg font-bold text-white mb-3" data-id="element-412">
          Using Python (OpenAI SDK)
        </h3>
        <div className="mb-6" data-id="element-413">
          <CodeBlock code={pythonCallCode} language="python" data-id="element-414" />
        </div>

        <h3 className="text-lg font-bold text-white mb-3" data-id="element-415">
          Using TypeScript (OpenAI SDK)
        </h3>
        <div className="mb-6" data-id="element-416">
          <CodeBlock code={tsCallCode} language="typescript" data-id="element-417" />
        </div>

        <h3 className="text-lg font-bold text-white mb-3" data-id="element-418">Streaming</h3>
        <p className="text-gate-text-secondary leading-relaxed mb-4" data-id="element-419">
          Add <IC data-id="element-420">"stream": true</IC> to receive server-sent events as the model
          generates tokens:
        </p>
        <CodeBlock code={streamCode} language="bash" data-id="element-421" />
      </section>

      {/* ── For Providers ── */}
      <section className="mb-14" data-id="element-422">
        <h2 id="for-providers" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-423">
          For Providers
        </h2>
        <p className="text-gate-text-secondary leading-relaxed" data-id="element-424">
          If you're a model provider interested in FPGA-optimized inference for
          your models, we'd love to hear from you. OpenFPGA handles the full
          synthesis pipeline — from model weights to optimized FPGA bitstreams.{' '}
          <a href="https://docs.google.com/forms/d/e/1FAIpQLSeA3akOwONwkYJLK4nX5RaNw1wlVWWLd6dPKvH2FSMPjvA9IA/viewform" target="_blank" rel="noopener noreferrer" className="text-gate-teal hover:underline font-medium" data-id="element-425">
            Get in touch
          </a>{' '}
          to discuss integration.
        </p>
      </section>
    </DocsLayout>;
}